import { getAppTokenApi } from "./authApi";
import { generateClientKeyPair, deriveSessionKey } from "./cryptoUtil";

const REFRESH_BEFORE_EXPIRY_MS = 60_000;
// Only a fallback: the real TTL comes from the app-token response, so the two sides can't
// drift into the client trusting a token the server already considers expired.
const FALLBACK_TOKEN_TTL_MS = 60 * 60 * 1000;

export interface AppSession {
  token: string;
  sessionKey: CryptoKey;
  expiresAt: number;
}

let session: AppSession | null = null;
let inflight: Promise<AppSession> | null = null;
let sessionExpiresAt = 0;

async function fetchSession(): Promise<AppSession> {
  const { clientPriv, clientPub } = await generateClientKeyPair();
  const { token, serverPub, expiresIn } = await getAppTokenApi(clientPub);
  const sessionKey = await deriveSessionKey(clientPriv, serverPub);
  // Measured from now rather than from the token's exp: a skewed client clock would
  // otherwise either refetch in a loop or keep using a token the server has retired.
  const ttlMs = expiresIn ? expiresIn * 1000 : FALLBACK_TOKEN_TTL_MS;
  return {
    token,
    sessionKey,
    expiresAt: Date.now() + ttlMs - REFRESH_BEFORE_EXPIRY_MS,
  };
}

export async function getAppSession(): Promise<AppSession> {
  if (session && session.expiresAt > Date.now()) return session;
  if (!inflight) {
    inflight = fetchSession()
      .then((s) => {
        session = s;
        inflight = null;
        return s;
      })
      .catch((err) => {
        inflight = null;
        throw err;
      });
  }
  return inflight;
}

export function setSessionExpiry(ts: number): void {
  sessionExpiresAt = ts;
}

export function getSessionExpiry(): number {
  return sessionExpiresAt;
}

// 0 means expiry is unknown (before first sign-in, and after every reload) and
// must count as due — treating it as not-expiring made one failed refresh
// permanent.
export function shouldRefreshSession(): boolean {
  if (sessionExpiresAt === 0) return true;
  return sessionExpiresAt - Date.now() < 5 * 60 * 1000;
}

export function resetSession(): void {
  session = null;
  inflight = null;
  sessionExpiresAt = 0;
}
